import { useState, useEffect, useMemo, useCallback } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import { FileUp, ArrowUpRight } from 'lucide-react';
import { useFileDialog } from '@/hooks/useFileDialog';
import { useAuth } from '@/hooks/useAuth';
import { getRecent } from '@/lib/recentFiles';
import { isProFeature, hasFeature } from '@/lib/license';
import { shortTitle } from './CategoryLauncher';
import {
  DASHBOARD_TABS,
  localizedLabel,
  localizedToolTitle,
  localizedToolDescription,
} from '@/lib/toolCategories';
import { buildAllTools, openPdfForMode, resolveToolClick } from '@/lib/toolLaunch';

// ------------------------------------------------------------------
// All tools — built from shared helper (keeps Dashboard in sync)
// ------------------------------------------------------------------
const ALL_TOOLS = buildAllTools();

// ------------------------------------------------------------------
// Greeting helpers
// ------------------------------------------------------------------
function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function formatRelativeTime(openedAt) {
  if (!openedAt) return '';
  const diff = Date.now() - openedAt;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

// ------------------------------------------------------------------
// DesktopHome
// ------------------------------------------------------------------
export default function DesktopHome() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openFile: openFileDialog } = useFileDialog();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [recentFiles, setRecentFiles] = useState([]);
  const [recentError, setRecentError] = useState(null);

  // Greeting
  const firstName = user?.displayName?.split(' ')[0] || 'there';

  // Load recent files
  useEffect(() => {
    let cancelled = false;
    getRecent().then((list) => {
      if (!cancelled) setRecentFiles(Array.isArray(list) ? list.slice(0, 4) : []);
    });
    return () => { cancelled = true; };
  }, []);

  // Open PDF via dialog (delegates route logic to shared helper)
  const onOpenFile = useCallback(async (mode) => {
    const r = await openFileDialog('.pdf');
    openPdfForMode(r, mode, navigate);
  }, [openFileDialog, navigate]);

  // Open a recent file (desktop path)
  const openRecent = useCallback(async (rf) => {
    if (!window.electronAPI?.readFile) return;
    setRecentError(null);
    try {
      const buffer = await window.electronAPI.readFile(rf.path);
      const file = new File([buffer], rf.name, { type: 'application/pdf' });
      navigate('/viewer', { state: { newDocument: true, file, filePath: rf.path } });
    } catch (err) {
      // Was a silent no-op — the user clicked and nothing happened. Say so.
      console.error('Failed to open recent file:', rf.path, err);
      setRecentError(rf.name);
    }
  }, [navigate]);

  // Filtered tool list
  const visibleTools = useMemo(
    () => (activeTab === 'all' ? ALL_TOOLS : ALL_TOOLS.filter((tool) => tool.tabId === activeTab)),
    [activeTab]
  );

  return (
    <div style={{ padding: '28px 32px 36px' }}>
      {/* ---- Greeting ---- */}
      <div style={{ fontSize: 21, fontWeight: 600, letterSpacing: '-0.4px', marginBottom: 3 }}>
        {getGreeting()}, {firstName}
      </div>
      <div style={{ fontSize: 13.5, color: 'var(--ds-ink-3, #8C8FA0)', marginBottom: 22 }}>
        Open a document to start, or pick up where you left off.
      </div>

      {/* ---- home-top: dropzone + recent files ---- */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.55fr 1fr',
          gap: 20,
          marginBottom: 30,
        }}
      >
        {/* Dropzone */}
        <div
          style={{
            border: '1.5px dashed var(--ds-line-3, #D2D3DD)',
            borderRadius: 12,
            background: 'var(--ds-surface-subtle, #FCFCFD)',
            padding: '34px 28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 54,
              height: 54,
              borderRadius: 14,
              background: 'var(--ds-indigo-50, #EEF0FE)',
              color: 'var(--ds-indigo, #4F46E5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
            }}
          >
            <FileUp size={25} strokeWidth={1.5} />
          </div>
          <h3 style={{ fontSize: 16.5, fontWeight: 600, letterSpacing: '-0.2px', marginBottom: 5 }}>
            Drop a PDF to get started
          </h3>
          <p style={{ fontSize: 13, color: 'var(--ds-ink-3, #8C8FA0)', marginBottom: 18 }}>
            Drag a file anywhere in this window, or open one from your computer.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={() => onOpenFile()}
              style={{
                height: 38,
                padding: '0 18px',
                borderRadius: 9,
                background: 'linear-gradient(140deg, var(--ds-indigo, #4F46E5), var(--ds-indigo-strong, #4338CA))',
                color: 'var(--ds-content, #fff)',
                border: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Open file
            </button>
          </div>
        </div>

        {/* Recent files */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 13 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--ds-ink-2, #565869)', letterSpacing: '0.1px' }}>
              Recent files
            </h4>
          </div>
          {recentError && (
            <div role="alert" style={{ fontSize: 12.5, color: 'var(--ds-danger, #DC2626)', marginBottom: 8 }}>
              Couldn’t open “{recentError}”. It may have been moved, renamed, or deleted.
            </div>
          )}
          {recentFiles.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--ds-ink-4, #A9ABB8)', paddingTop: 8 }}>
              No recent files yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentFiles.map((rf, i) => (
                <button
                  key={rf.path || rf.name || i}
                  type="button"
                  onClick={() => openRecent(rf)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '9px 11px',
                    border: '1px solid var(--ds-line, #EAEBF0)',
                    borderRadius: 10,
                    background: 'var(--ds-content, #fff)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  {/* PDF thumbnail stub */}
                  <div
                    style={{
                      width: 34,
                      height: 42,
                      borderRadius: 5,
                      background: 'var(--ds-content, #fff)',
                      border: '1px solid var(--ds-line-2, #DEDFE7)',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'flex-start',
                      padding: '0 0 4px 4px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 7,
                        fontWeight: 700,
                        color: '#fff',
                        background: 'var(--ds-coral, #EF6A5A)',
                        borderRadius: 3,
                        padding: '1px 3px',
                        letterSpacing: '0.3px',
                      }}
                    >
                      PDF
                    </span>
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {rf.name}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--ds-ink-3, #8C8FA0)' }}>
                      {formatRelativeTime(rf.openedAt)}
                    </div>
                  </div>
                  <ArrowUpRight size={16} style={{ color: 'var(--ds-ink-4, #A9ABB8)', flexShrink: 0 }} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---- Tool section header + chips ---- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h4 style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.2px' }}>All tools</h4>
        <div style={{ display: 'flex', gap: 7, marginLeft: 8, flexWrap: 'wrap' }}>
          {DASHBOARD_TABS.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: isActive ? 'var(--ds-content, #fff)' : 'var(--ds-ink-2, #565869)',
                  background: isActive ? 'var(--ds-ink, #191A24)' : 'var(--ds-content, #fff)',
                  border: `1px solid ${isActive ? 'var(--ds-ink, #191A24)' : 'var(--ds-line-2, #DEDFE7)'}`,
                  padding: '5px 12px',
                  borderRadius: 20,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {localizedLabel(t, tab)}
              </button>
            );
          })}
        </div>
      </div>

      {/* ---- Tool grid ---- */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4,1fr)',
          gap: 13,
        }}
      >
        {visibleTools.map((tool) => (
          <ToolCard key={`${tool.title}-${tool.tabId}`} tool={tool} onOpenFile={onOpenFile} t={t} />
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// ToolCard — mirrors Dashboard.jsx ToolCard semantics exactly
// ------------------------------------------------------------------
function ToolCard({ tool, onOpenFile, t }) {
  const navigate = useNavigate();
  const { icon: Icon, feature, pro, colors } = tool;
  const title = shortTitle(localizedToolTitle(t, tool));
  const description = localizedToolDescription(t, tool);
  const isPro = pro || (feature && isProFeature(feature));
  const locked = isPro && !hasFeature(feature);

  const handleClick = () => resolveToolClick(tool, { onOpenFile, navigate });

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        position: 'relative',
        border: '1px solid var(--ds-line, #EAEBF0)',
        borderRadius: 12,
        padding: '15px 15px 16px',
        background: 'var(--ds-content, #fff)',
        cursor: 'pointer',
        textAlign: 'left',
        opacity: locked ? 0.6 : 1,
        fontFamily: 'inherit',
      }}
    >
      {isPro && (
        <span
          style={{
            position: 'absolute',
            top: 13,
            right: 13,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.4px',
            color: 'var(--ds-indigo-strong, #4338CA)',
            background: 'var(--ds-indigo-50, #EEF0FE)',
            border: '1px solid var(--ds-indigo-100, #E2E5FC)',
            padding: '1px 6px',
            borderRadius: 5,
          }}
        >
          PRO
        </span>
      )}
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 12,
          background: colors?.bg || 'var(--ds-indigo-50, #EEF0FE)',
          color: colors?.fg || 'var(--ds-indigo, #4F46E5)',
        }}
      >
        {Icon && <Icon size={19} strokeWidth={1.5} />}
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.1px', marginBottom: 3 }}>
        {title}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ds-ink-3, #8C8FA0)', lineHeight: 1.45 }}>
        {description}
      </div>
    </button>
  );
}
